import React, { useCallback, useEffect, useState } from 'react';
import DefaultAvatar from 'assets/images/default-avatar.png';
import { useNavigate } from 'react-router-dom';
import { AddEditPFAdminModal, IconBell, IconPen, IconSmallArrow, OrangeButton } from 'components';
import {
  getRequestData,
  toggleUserNotificationsByAdmin,
  toggleUserNotificationsByManager,
} from 'api/api';
import { endpoints } from 'api/endpoints';
import { getIdFromPath } from 'utils/helpers';
import { ROLE_TYPES } from 'api/constants';
import { Switch } from 'antd';

export const ManagerProfile = ({ role }) => {
  const navigate = useNavigate();
  const userID = getIdFromPath(location.pathname);
  const [userData, setUserData] = useState();
  const [isEditMode, setIsEditMode] = useState(false);

  const getUser = useCallback(
    async (userID) => {
      try {
        const { data } = await getRequestData(endpoints.getUser(userID), role);
        return data;
      } catch (error) {
        console.error('Error fetching user data:', error);
        return null;
      }
    },
    [role],
  );

  useEffect(() => {
    (async () => {
      const data = await getUser(userID);
      if (data) {
        setUserData(data.profile);
      }
    })();
  }, [getUser, userID]);

  const handleNotificationToggle = async (checked, clientId) => {
    setUserData((prevData) => ({
      ...prevData,
      email_notification_enabled: checked,
    }));
    if (role === ROLE_TYPES.slh_manager) {
      await toggleUserNotificationsByManager(
        clientId,
        { email: checked, sms: false },
        () => {},
        () => {
          setUserData((prevData) => ({
            ...prevData,
            email_notification_enabled: !checked,
          }));
        },
      );
    } else {
      await toggleUserNotificationsByAdmin(clientId, { email: checked, sms: false });
    }
  };

  return (
    userData && (
      <div className="flex flex-col flex-1 gap-y-6">
        <AddEditPFAdminModal
          onSuccess={() => getUser(userID).then((data) => setUserData(data.profile))}
          isOpen={isEditMode}
          onClose={() => {
            setIsEditMode(false);
          }}
          adminData={userData}
          isEditMode={isEditMode}
          role={ROLE_TYPES.slh_manager}
          slhPartnerId={userData.partner_id[0]}
          isInAdminViewPage
        />
        <div className="flex justify-between w-full">
          <div className="flex items-center gap-x-4">
            <div
              onClick={() => navigate(-1)}
              className="w-10 h-10 bg-white rounded-lg shadow-block-shadow flex items-center justify-center cursor-pointer"
            >
              <IconSmallArrow className="w-2 h-4" />
            </div>
            <p className="nunito-title">
              {userData.firstname} {userData.lastname}
            </p>
          </div>
          <OrangeButton onClick={() => setIsEditMode(true)} label="Edit" icon={<IconPen />} />
        </div>
        <div className={'w-full flex flex-col gap-6 p-8 bg-white rounded-[60px]'}>
          <div className="flex gap-x-6 items-start">
            {/* Left: avatar */}
            <div className="px-6">
              <img
                src={userData.avatar || DefaultAvatar}
                alt="avatar"
                width={156}
                height={156}
                className="rounded-xl object-cover"
              />
            </div>

            <div className="flex-1 flex gap-x-6">
              <div className="flex-1 flex flex-col gap-3">
                <p className="text-base font-medium text-blue capitalize">General information</p>
                <div className="flex flex-col gap-2">
                  <div className="flex flex-col items-start gap-y-1">
                    <span className="text-14 text-darkGrey">First name</span>
                    <span className="text-14 font-medium">{userData.firstname || '-'}</span>
                  </div>
                  <div className="flex flex-col items-start gap-y-1">
                    <span className="text-14 text-darkGrey">Last name</span>
                    <span className="text-14 font-medium">{userData.lastname || '-'}</span>
                  </div>
                  <div className="flex flex-col items-start gap-y-1">
                    <span className="text-14 text-darkGrey">Role</span>
                    <span className="text-14 font-medium capitalize">
                      {userData.role.replace('_', ' ') || '-'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex-1 flex flex-col gap-3">
                <p className="text-base font-medium text-blue capitalize">Account details</p>
                <div className="flex flex-col gap-2">
                  <div className="flex flex-col items-start gap-y-1">
                    <span className="text-14 text-darkGrey">Email</span>
                    <span className="text-14 font-medium">{userData.email || '-'}</span>
                  </div>
                  <div className="flex flex-col items-start gap-y-1">
                    <span className="text-14 text-darkGrey">Phone</span>
                    <span className="text-14 font-medium">
                      {userData.country_code}
                      {userData.phone || '-'}
                    </span>
                  </div>
                  {userData.status && (
                    <div className="flex flex-col items-start gap-y-1">
                      <span className="text-14 text-darkGrey">Status</span>
                      <span className="text-14 font-medium capitalize">
                        {userData.status || '-'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <span className="border-t border-color-divider" />
          <div className="flex flex-col w-full items-start gap-y-4">
            <div className="flex items-center gap-x-2">
              <IconBell className="w-6 h-6" />
              <p className="text-base font-medium">Notification settings</p>
            </div>
            <div className="flex items-center gap-x-4">
              <div
                className={`flex items-center gap-x-2 rounded-xl px-4 py-2.5 ${userData?.email_notification_enabled ? 'bg-light' : 'bg-white'} border border-color-divider`}
              >
                <Switch
                  checked={!!userData?.email_notification_enabled}
                  onChange={(checked) => handleNotificationToggle(checked, userData.user_id)}
                />
                <p className="text-base font-medium">Email notifications</p>
              </div>
              <div
                className={`flex items-center gap-x-2 rounded-xl px-4 py-2.5 ${userData?.sms_notification_enabled ? 'bg-light' : 'bg-white'} border border-color-divider`}
              >
                <Switch
                  checked={!!userData?.sms_notification_enabled}
                  disabled
                  onChange={(checked) => handleNotificationToggle(checked, userData.user_id)}
                />
                <p className="text-base font-medium">SMS notifications</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  );
};
